import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR } from "@/lib/api";
import { toast } from "sonner";
import { X, PlusCircle, Loader2, Sparkles, User, ArrowRight } from "lucide-react";
import InvoiceShareModal from "@/components/InvoiceShareModal";

/**
 * Add an additional service to an existing transaction.
 * The therapist must already exist in the transaction.
 * After adding, that therapist's 5% base commission on ALL their items in this
 * tx is replaced with a 10% bonus commission on each (combined total*10%).
 *
 * Props:
 *   tx        — transaction to add to
 *   onClose()
 *   onDone(updatedTx)
 */
export default function AddServiceModal({ tx, onClose, onDone }) {
  const { t, lang } = useLang();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [therapistId, setTherapistId] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedTx, setSavedTx] = useState(null);

  // Unique therapists already in this transaction
  const therapistsInTx = useMemo(() => {
    const map = new Map();
    tx.items.forEach((it) => {
      if (!map.has(it.therapist_id)) {
        map.set(it.therapist_id, { id: it.therapist_id, name: it.therapist_name });
      }
    });
    return Array.from(map.values());
  }, [tx]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    if (therapistsInTx.length === 1) setTherapistId(therapistsInTx[0].id);
  }, [therapistsInTx]);

  const selectedSvc = services.find((s) => s.id === serviceId);
  const selectedTherapistItems = tx.items.filter((it) => it.therapist_id === therapistId);
  const sameTherapistBaseSum = selectedTherapistItems.reduce((s, it) => s + it.price, 0);
  const projectedAddon = selectedSvc?.price || 0;
  const projectedCommission = (sameTherapistBaseSum + projectedAddon) * 0.10;
  const projectedNewTotal = tx.subtotal + projectedAddon - tx.discount_amount;

  const svcLabel = (s) => `${lang === "ar" ? s.name_ar : (lang === "en" ? (s.name_en || s.name_id) : s.name_id)} — ${formatSAR(s.price)}`;

  async function submit() {
    if (!therapistId) return toast.error(t("addsvc.therapist"));
    if (!serviceId) return toast.error(t("addsvc.service"));
    setBusy(true);
    try {
      const { data } = await api.post(`/transactions/${tx.id}/add-service`, {
        service_id: serviceId,
        therapist_id: therapistId,
      });
      toast.success(t("addsvc.success"));
      setSavedTx(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  }

  // After add: show the InvoiceShareModal so kasir bisa cetak / kirim WA struk transaksi baru.
  if (savedTx) {
    const m = savedTx.payment_method || "cash";
    const payLabel = m === "split"
      ? `${t("kasir.paySplit")} · ${t("kasir.payCash")} ${formatSAR(savedTx.cash_amount || 0)} + ${t("kasir.payCard")} ${formatSAR(savedTx.card_amount || 0)}`
      : (m === "card" ? t("kasir.payCard") : t("kasir.payCash"));
    return (
      <InvoiceShareModal
        tx={savedTx}
        customerName={savedTx.customer_name || ""}
        paymentMethod={payLabel}
        onClose={() => {
          setSavedTx(null);
          onDone?.(savedTx);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-3 md:p-4" onClick={onClose}>
      <div className="card-luxury w-full max-w-lg max-h-[92vh] overflow-y-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-xl font-heading flex items-center gap-2">
              <PlusCircle className="h-5 w-5 text-leaf" /> {t("addsvc.title")}
            </h2>
            <button onClick={onClose}><X className="h-5 w-5" /></button>
          </div>

          {/* Transaction summary */}
          <div className="bg-charcoal-900/60 border border-gold/15 rounded-lg p-3 text-sm mb-4">
            <div className="text-xs text-foreground/50 uppercase tracking-[0.2em] mb-2">Transaksi Saat Ini</div>
            <div>📞 {tx.customer_phone}</div>
            <div className="text-foreground/70 mt-1">{tx.items.map((i) => `${i.service_name_id} → ${i.therapist_name}`).join(", ")}</div>
            <div className="mt-2 pt-2 border-t border-gold/10 flex justify-between">
              <span className="text-foreground/60">{t("kasir.total")}</span>
              <span className="text-gold font-heading">{formatSAR(tx.total)}</span>
            </div>
          </div>

          {/* Therapist picker */}
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("addsvc.therapist")}</label>
            <div className="grid grid-cols-2 gap-2">
              {therapistsInTx.map((th) => (
                <label
                  key={th.id}
                  data-testid={`addsvc-therapist-${th.id}`}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition ${
                    therapistId === th.id ? "border-gold bg-gold/10" : "border-charcoal-600 hover:border-gold/40"
                  }`}
                >
                  <input type="radio" name="th" checked={therapistId === th.id} onChange={() => setTherapistId(th.id)} className="accent-gold" />
                  <User className="h-4 w-4 text-gold" />
                  <span className="text-sm font-medium truncate">{th.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Service picker */}
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("addsvc.service")}</label>
            <select
              data-testid="addsvc-service"
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 focus:border-gold rounded-lg px-3 py-2.5"
            >
              <option value="">— {t("kasir.selectService")} —</option>
              {services.map((s) => <option key={s.id} value={s.id}>{svcLabel(s)}</option>)}
            </select>
          </div>

          {/* Commission preview */}
          <div className="bg-leaf/8 border border-leaf/30 rounded-lg p-3 mb-4 space-y-2">
            <div className="text-xs text-leaf">{t("addsvc.commissionInfo")}</div>
            {selectedSvc && therapistId && (
              <div className="text-xs space-y-1 pt-2 border-t border-leaf/15">
                <div className="flex justify-between"><span className="text-foreground/60">Layanan awal terapis</span><span>{formatSAR(sameTherapistBaseSum)}</span></div>
                <div className="flex justify-between"><span className="text-foreground/60">Layanan tambahan</span><span>{formatSAR(projectedAddon)}</span></div>
                <div className="flex justify-between pt-1 border-t border-leaf/10">
                  <span className="text-foreground/80">{t("addsvc.newCommission")} <span className="text-leaf">(10%)</span></span>
                  <span className="text-leaf font-heading">{formatSAR(projectedCommission)}</span>
                </div>
                <div className="flex justify-between text-xs pt-1">
                  <span className="text-foreground/60">{t("addsvc.newTotal")}</span>
                  <span className="text-gold font-medium">{formatSAR(Math.max(projectedNewTotal, 0))}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="btn-outline-gold flex-1 justify-center">{t("common.cancel")}</button>
            <button
              onClick={submit}
              disabled={busy || !therapistId || !serviceId}
              data-testid="addsvc-confirm-btn"
              className="btn-leaf flex-1 justify-center disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {t("addsvc.confirmReceipt")} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
